import { useEffect, useRef, useState } from "react";
import { Send, ArrowRight, Pencil, Info } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";

const STATUS_LABEL = {
  paid: "Payment done", waiting: "Awaiting payment", dispute: "In dispute",
  task_confirmed: "Task confirmed", payment_confirmed: "Payment confirmed",
};

function StatusChip({ value }) {
  const cls = value === "paid" || value === "payment_confirmed" ? "bg-emerald-100 text-emerald-700"
    : value === "dispute" ? "bg-red-100 text-red-700"
    : value === "task_confirmed" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700";
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>{STATUS_LABEL[value] || value}</span>;
}

export function InvoiceHistoryList({ history = [] }) {
  const [detail, setDetail] = useState(null); // history entry whose diff is open
  if (!history.length) {
    return <div className="text-sm text-zinc-500" data-testid="inv-history-empty">No history recorded yet.</div>;
  }
  return (
    <>
    <ol className="space-y-2 text-sm" data-testid="inv-history-list">
      {history.map((h, idx) => (
        <li key={idx} className="flex items-start gap-3 rounded-md border border-zinc-200 bg-white p-2.5" data-testid={`inv-history-row-${idx}`}>
          <div className="text-[11px] text-zinc-500 w-24 shrink-0">{(h.at || "").slice(0, 16).replace("T", " ")}</div>
          <div className="flex-1 flex items-center gap-2 flex-wrap">
            {h.action === "edited" ? (
              <span className="flex items-center gap-1 text-zinc-700">
                <Pencil className="h-3 w-3 text-zinc-400" /> Edited {(h.fields || []).join(", ")}
                {(h.details || []).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setDetail(h)}
                    className="ml-1 text-sky-600 hover:text-sky-800"
                    title="Show what was changed"
                    data-testid={`inv-history-info-${idx}`}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ) : h.from ? (
              <><StatusChip value={h.from} /><ArrowRight className="h-3 w-3 text-zinc-400" /><StatusChip value={h.to} /></>
            ) : (
              <span className="flex items-center gap-1"><span className="text-zinc-500">Created</span> <StatusChip value={h.to} /></span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 max-w-[8rem] truncate">{h.by_name || "—"}</div>
        </li>
      ))}
    </ol>
    <HistoryChangesDialog entry={detail} onClose={() => setDetail(null)} />
    </>
  );
}

function ItemsSnapshot({ label, items }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2">
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">{label}</div>
      {(items || []).length === 0 ? (
        <div className="text-xs text-zinc-400">no items</div>
      ) : (
        <ul className="space-y-0.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-baseline justify-between gap-2 text-xs text-zinc-700">
              <span><span className="text-zinc-400 mr-1">{i + 1}.</span>{it.text}</span>
              {it.price != null && <span className="text-zinc-500 whitespace-nowrap">{Number(it.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HistoryChangesDialog({ entry, onClose }) {
  if (!entry) return null;
  const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="inv-history-changes-dialog">
        <DialogHeader>
          <DialogTitle>Invoice changes</DialogTitle>
          <DialogDescription>
            By {entry.by_name || "supplier"} · {(entry.at || "").slice(0, 16).replace("T", " ")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(entry.details || []).map((d, i) => (
            <div key={i} data-testid={`inv-history-change-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">{d.field}</div>
              {d.from_items || d.to_items ? (
                <div className="space-y-2">
                  <ItemsSnapshot label="Before" items={d.from_items} />
                  <ItemsSnapshot label="After" items={d.to_items} />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-zinc-800">
                  <span className="line-through text-zinc-400">{fmt(d.from)}</span>
                  <ArrowRight className="h-3 w-3 text-zinc-400 shrink-0" />
                  <span className="font-medium">{fmt(d.to)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="inv-history-changes-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InvoiceMessages({ invoiceId, initialMessages = [], mySide, onUpdated }) {
  const [messages, setMessages] = useState(initialMessages);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  // Re-sync when the parent hands us a refreshed invoice (e.g. a dispute
  // reason was just appended server-side). Keyed on length + last id so a
  // fresh `[]` default per render can't loop the effect.
  const lastMsgId = initialMessages.length ? initialMessages[initialMessages.length - 1].id : null;
  useEffect(() => { setMessages(initialMessages); }, [invoiceId, initialMessages.length, lastMsgId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!invoiceId) return;
    api.post(`/invoices/${invoiceId}/messages/read`).catch(() => {});
  }, [invoiceId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/messages`, { text: t });
      setMessages(data.messages || []);
      setText("");
      onUpdated?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="inv-messages">
      <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
        {messages.length === 0 ? (
          <div className="text-sm text-zinc-500" data-testid="inv-messages-empty">No messages yet. Start the conversation below.</div>
        ) : messages.map((m) => {
          const mine = m.by === mySide;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`inv-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${mine ? "bg-ink text-white rounded-br-sm" : "bg-zinc-100 text-zinc-900 rounded-bl-sm"}`}>
                <div className="text-[10px] opacity-70 mb-0.5">{m.name} · {(m.at || "").slice(5, 16).replace("T", " ")}</div>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="mt-2 flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Write a message…"
          className="flex-1 resize-none rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ink/20"
          data-testid="inv-msg-input"
        />
        <Button onClick={send} disabled={sending || !text.trim()} className="bg-ink hover:bg-black shrink-0" data-testid="inv-msg-send">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
